// src/pages/WhatIsPBIS.js
import React from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";

const WhatIsPBIS = () => {
  const navigate = useNavigate();

  const navButtonStyle = {
    background: "transparent",
    color: "rgba(255,255,255,0.85)",
    border: "none",
    fontSize: "1rem",
    cursor: "pointer",
    fontWeight: "500",
    textDecoration: "none",
    transition: "all 0.2s ease",
  };

  const handleHover = (e, entering) => {
    e.target.style.color = entering ? "#ffffff" : "rgba(255,255,255,0.85)";
    e.target.style.transform = entering ? "translateY(-1px)" : "translateY(0)";
  };

  return (
    <>
      <Helmet>
        <title>What is PBIS? | PBIS Portal</title>
        <meta
          name="description"
          content="Learn what PBIS is, how it works in schools, and how to actually implement it in your school."
        />
        <link rel="canonical" href="https://pbisportal.com/what-is-pbis" />
      </Helmet>

      <div
        style={{
          fontFamily: "'Segoe UI', sans-serif",
          backgroundColor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* HEADER */}
        <header style={headerStyle}>
          <Link to="/" style={logoStyle}>
            🎯 PBIS Portal
          </Link>

          <div style={navContainer}>
            <NavButton label="About" to="/about" {...{ navigate, navButtonStyle, handleHover }} />
            <NavButton label="Features" to="/features" {...{ navigate, navButtonStyle, handleHover }} />
            <NavButton label="What is PBIS" to="/what-is-pbis" {...{ navigate, navButtonStyle, handleHover }} />
            <NavButton label="Contact" to="/contact" {...{ navigate, navButtonStyle, handleHover }} />

            <div style={divider} />

            <Link to="/login" style={loginButton}>
              Log In
            </Link>
          </div>
        </header>

        {/* HERO */}
        <section style={heroStyle}>
          <h1 style={heroTitle}>What is PBIS?</h1>

          <p style={heroText}>
            PBIS (Positive Behavioral Interventions and Supports) is a simple, research-backed
            approach that helps schools improve student behavior, build a positive culture,
            and support all students — not just react to problems.
          </p>
        </section>

        {/* CONTENT */}
        <section style={contentContainer}>

          <SectionCard
            title="What does PBIS actually mean?"
            content="At its core, PBIS is about being proactive instead of reactive. Instead of only responding to negative behavior, schools clearly teach expectations and consistently recognize students when they meet them."
          />

          <SectionCard
            title="What does this look like in a real school?"
            list={[
              "Clear, school-wide expectations (like Respect, Responsibility, Safety)",
              "Students are taught what those expectations look like",
              "Positive behavior is recognized (tickets, rewards, praise)",
              "Staff respond consistently across classrooms",
              "Data is used to guide decisions",
            ]}
          />

          <SectionCard
            title="How to run PBIS in your school (simple version)"
            content="Getting started with PBIS doesn’t have to be complicated. The goal is consistency, not perfection."
            list={[
              " Define your expectations (3–5 clear values)",
              " Teach those expectations to students",
              " Recognize positive behavior consistently",
              " Track progress and adjust as needed",
              "The most important factor is consistency across staff.",
            ]}
          />

          <SectionCard
            title="Why does PBIS work?"
            content="PBIS works because it focuses on reinforcing the behaviors you want to see more of. When students are recognized for positive actions, they are more likely to repeat them — which leads to stronger school culture over time."
          />

          <SectionCard
            title="Research-backed results"
            list={[
              "Reduced behavioral incidents",
              "Improved school climate",
              "Better academic outcomes",
              "Increased student engagement",
            ]}
          />

          <SectionCard
            title="How PBIS Portal fits in"
            content="PBIS Portal helps schools actually run PBIS day-to-day. It makes it easy to give tickets, track behavior, run a student rewards store, and see real-time data — without spreadsheets or paper systems."
          />

        </section>
      </div>
    </>
  );
};

// 🔹 Reusable Nav Button
const NavButton = ({ label, to, navButtonStyle, handleHover }) => (
  <Link
    to={to}
    style={navButtonStyle}
    onMouseEnter={(e) => handleHover(e, true)}
    onMouseLeave={(e) => handleHover(e, false)}
  >
    {label}
  </Link>
);

// 🔹 Section Card
const SectionCard = ({ title, content, list }) => (
  <div style={cardStyle}>
    <h2 style={sectionTitle}>{title}</h2>

    {content && <p style={paragraph}>{content}</p>}

    {list && (
      <ul style={listStyle}>
        {list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    )}
  </div>
);

// 🎨 Styles
const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "1rem 2rem",
  backgroundColor: "#1e3a8a",
  color: "white",
  position: "sticky",
  top: 0,
  zIndex: 1000,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
};

const logoStyle = {
  fontSize: "1.8rem",
  fontWeight: "700",
  cursor: "pointer",
  color: "inherit",
  textDecoration: "none",
};

const navContainer = {
  display: "flex",
  alignItems: "center",
  gap: "1.5rem",
};

const divider = {
  width: "1px",
  height: "20px",
  background: "rgba(255,255,255,0.3)",
};

const loginButton = {
  backgroundColor: "#2563eb",
  color: "#fff",
  border: "none",
  padding: "0.65rem 1.2rem",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "600",
};

const heroStyle = {
  textAlign: "center",
  padding: "4rem 2rem",
  background: "linear-gradient(135deg, #e0f2fe 0%, #ecfeff 100%)",
};

const heroTitle = {
  fontSize: "2.8rem",
  fontWeight: "800",
  marginBottom: "1rem",
};

const heroText = {
  fontSize: "1.2rem",
  color: "#475569",
  maxWidth: "700px",
  margin: "0 auto",
  lineHeight: "1.7",
};

const contentContainer = {
  maxWidth: "900px",
  margin: "3rem auto",
  padding: "0 1rem",
};

const cardStyle = {
  backgroundColor: "#ffffff",
  padding: "1.75rem",
  borderRadius: "14px",
  marginBottom: "1.5rem",
  boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
};

const sectionTitle = {
  marginBottom: "0.75rem",
  color: "#1e293b",
};

const paragraph = {
  color: "#475569",
  lineHeight: "1.7",
};

const listStyle = {
  marginTop: "0.5rem",
  color: "#475569",
  lineHeight: "1.7",
};

export default WhatIsPBIS;
